import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '../components/Navbar';
import { useClient } from '../context/ClientContext';
import { useToast } from '../hooks/use-toast';
import { formatET } from '../lib/timezone';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { FileUpload } from '../components/FileUpload';
import { filesApi } from '../services/api';
import {
  Save, Palette, Hash, AtSign, Image, ExternalLink,
  Loader2, Trash2, Plus, X, MessageSquare, Link2, Copy, RefreshCw, Check, AlertTriangle, Mail, Eye
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ColorPicker({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className="w-8 h-8 rounded-lg border-2 border-white shadow-sm cursor-pointer"
        style={{ backgroundColor: value }}
        onClick={() => document.getElementById(testId)?.click()}
      />
      <input
        id={testId}
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="sr-only"
      />
      <div className="flex-1">
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</label>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="mt-0.5 font-mono text-xs h-8"
          data-testid={testId}
        />
      </div>
    </div>
  );
}

function PortalActivityLog({ clientId, primaryColor }) {
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    axios.get(`${API}/clients/${clientId}/portal-activity`, { withCredentials: true })
      .then(res => setActivity(res.data))
      .catch(() => setActivity(null))
      .finally(() => setLoading(false));
  }, [clientId]);

  const actionLabels = {
    post_viewed: { label: 'Viewed post', color: 'text-blue-600 bg-blue-50' },
    post_approved: { label: 'Approved post', color: 'text-emerald-600 bg-emerald-50' },
    post_approved_with_edits: { label: 'Approved with edits', color: 'text-blue-600 bg-blue-50' },
    post_do_not_run: { label: 'Marked do not run', color: 'text-red-600 bg-red-50' },
    post_changes_requested: { label: 'Requested changes', color: 'text-orange-600 bg-orange-50' },
  };

  const formatTime = (ts) => {
    try {
      const d = new Date(ts);
      const now = new Date();
      const diffMs = now - d;
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHrs = Math.floor(diffMins / 60);
      if (diffHrs < 24) return `${diffHrs}h ago`;
      const diffDays = Math.floor(diffHrs / 24);
      if (diffDays < 7) return `${diffDays}d ago`;
      return formatET(ts, 'MMM d, yyyy') || ts;
    } catch { return ts; }
  };

  return (
    <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4" data-testid="portal-activity-section">
      <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
        <Eye className="w-4 h-4 text-gray-400" /> Portal Activity
      </h2>
      {loading ? (
        <p className="text-xs text-gray-400">Loading...</p>
      ) : !activity ? (
        <p className="text-xs text-gray-400">No activity data yet. Activity is logged once a client visits their portal.</p>
      ) : (
        <div className="space-y-3">
          {/* Summary */}
          <div className="flex items-center gap-4 text-xs">
            <div className="flex-1 bg-gray-50 rounded-lg p-3">
              <p className="text-gray-400 text-[10px] uppercase tracking-wider font-bold mb-0.5">Last Visit</p>
              <p className="text-gray-900 font-semibold">{activity.last_visit ? formatTime(activity.last_visit) : 'Never'}</p>
            </div>
            <div className="flex-1 bg-gray-50 rounded-lg p-3">
              <p className="text-gray-400 text-[10px] uppercase tracking-wider font-bold mb-0.5">Total Visits</p>
              <p className="text-gray-900 font-semibold">{activity.visit_count || 0}</p>
            </div>
          </div>

          {/* Recent actions */}
          {activity.recent_actions?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase text-gray-400 tracking-wider mb-2">Recent Activity</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {activity.recent_actions.map((a, i) => {
                  const config = actionLabels[a.action] || { label: a.action, color: 'text-gray-600 bg-gray-50' };
                  return (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className={`shrink-0 px-2 py-0.5 rounded text-[10px] font-bold ${config.color}`}>
                        {config.label}
                      </span>
                      {a.post_title && (
                        <span className="text-gray-600 truncate flex-1">"{a.post_title}"</span>
                      )}
                      <span className="text-gray-400 shrink-0 text-[10px]">{formatTime(a.timestamp)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function ConnectedAccounts({ clientKey }) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    setStatus(null);
    axios.get(`${API}/ayrshare/status`).then(r => setStatus(r.data)).catch(() => setStatus({ error: true }));
  }, [clientKey]);
  if (!status) return <p className="text-xs text-gray-400">Checking connected accounts…</p>;
  const names = Array.isArray(status.display_names) ? status.display_names : [];
  if (!status.has_profile_key || status.error || names.length === 0) {
    return (
      <div className="text-xs rounded-lg px-3 py-2 bg-amber-50 text-amber-800 border border-amber-200" data-testid="ayrshare-connected-accounts">
        {status.has_profile_key ? 'No social accounts found for this Profile Key.' : 'Not connected. Posts for this client will not publish until a Profile Key is saved.'}
      </div>
    );
  }
  return (
    <div className="text-xs rounded-lg px-3 py-2 bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid="ayrshare-connected-accounts">
      <p className="font-semibold mb-1">Posts for this client will go to:</p>
      {names.map((d, i) => (
        <p key={i} className="capitalize">{d.platform}: <strong className="normal-case">{d.displayName || d.username || 'Unnamed account'}</strong></p>
      ))}
      <p className="text-emerald-700/70 mt-1">If this isn't the right page, fix the Profile Key before publishing.</p>
    </div>
  );
}

export default function ClientSettingsPage() {
  const { activeClient, refreshClients } = useClient();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [primaryColor, setPrimaryColor] = useState('#1F467C');
  const [secondaryColor, setSecondaryColor] = useState('#5CB85C');
  const [accentColor, setAccentColor] = useState('#3980C3');
  const [hashtags, setHashtags] = useState([]);
  const [newHashtag, setNewHashtag] = useState('');
  const [socialHandles, setSocialHandles] = useState({});
  const [ayrshareProfileKey, setAyrshareProfileKey] = useState('');
  const [brandVoice, setBrandVoice] = useState('');
  const [saving, setSaving] = useState(false);
  const [hashtagGroups, setHashtagGroups] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupTags, setNewGroupTags] = useState('');
  const [editingGroup, setEditingGroup] = useState(null);
  const [portalToken, setPortalToken] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalCopied, setPortalCopied] = useState(false);
  const [portalWelcome, setPortalWelcome] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Load hashtag groups
  const loadHashtagGroups = async () => {
    try {
      const res = await axios.get(`${API}/hashtag-groups`);
      setHashtagGroups(res.data.groups || []);
    } catch {}
  };

  useEffect(() => {
    if (activeClient) {
      setName(activeClient.name || '');
      setLogoUrl(activeClient.logo_url || '');
      setPrimaryColor(activeClient.primary_color || '#1F467C');
      setSecondaryColor(activeClient.secondary_color || '#5CB85C');
      setAccentColor(activeClient.accent_color || '#3980C3');
      setHashtags(activeClient.hashtags || []);
      setSocialHandles(activeClient.social_handles || {});
      setAyrshareProfileKey(activeClient.ayrshare_profile_key || '');
      setBrandVoice(activeClient.brand_voice || '');
      // Load logo preview
      if (activeClient.logo_url && !activeClient.logo_url.startsWith('/')) {
        filesApi.get(activeClient.logo_url).then(url => setLogoPreview(url));
      } else {
        setLogoPreview(activeClient.logo_url);
      }
    }
  }, [activeClient]);

  useEffect(() => {
    loadHashtagGroups();
  }, [activeClient]);

  // Load portal token
  useEffect(() => {
    if (!activeClient) return;
    axios.get(`${API}/clients/${activeClient.id}/portal-token`)
      .then(res => setPortalToken(res.data.portal_token || null))
      .catch(() => setPortalToken(null));
    setPortalWelcome(activeClient.portal_welcome || '');
  }, [activeClient]);

  const handleGeneratePortalToken = async () => {
    if (!activeClient) return;
    setPortalLoading(true);
    try {
      const res = await axios.post(`${API}/clients/${activeClient.id}/portal-token`);
      setPortalToken(res.data.portal_token);
      toast({ title: 'Portal link generated', description: 'Share this link with your client' });
    } catch {
      toast({ title: 'Error', description: 'Failed to generate link', variant: 'destructive' });
    } finally {
      setPortalLoading(false);
    }
  };

  const clientSlug = activeClient?.slug || activeClient?.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || '';
  const portalUrl = portalToken
    ? clientSlug
      ? `${window.location.origin}/portal/${clientSlug}/${portalToken}`
      : `${window.location.origin}/portal/${portalToken}`
    : null;

  const handleCopyPortalLink = () => {
    if (!portalUrl) return;
    navigator.clipboard.writeText(portalUrl);
    setPortalCopied(true);
    setTimeout(() => setPortalCopied(false), 2000);
    toast({ title: 'Copied!', description: 'Portal link copied to clipboard' });
  };

  const handleDeleteClient = async () => {
    if (!activeClient || deleteConfirmText !== activeClient.name) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/clients/${activeClient.id}`, { withCredentials: true });
      toast({ title: 'Client deleted', description: `"${activeClient.name}" and all its data have been permanently removed.` });
      setDeleteConfirmOpen(false);
      setDeleteConfirmText('');
      await refreshClients();
      navigate('/dashboard');
    } catch (err) {
      toast({ title: 'Delete failed', description: err.response?.data?.detail || 'Something went wrong', variant: 'destructive' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleLogoUpload = async (fileData) => {
    if (!fileData) {
      setLogoUrl('');
      setLogoPreview(null);
      return;
    }
    setLogoUrl(fileData.id);
    const url = await filesApi.get(fileData.id);
    setLogoPreview(url);
  };

  const handleAddHashtag = () => {
    if (!newHashtag.trim()) return;
    let tag = newHashtag.trim();
    if (!tag.startsWith('#')) tag = '#' + tag;
    if (!hashtags.includes(tag)) {
      setHashtags(prev => [...prev, tag]);
    }
    setNewHashtag('');
  };

  const handleRemoveHashtag = (idx) => {
    setHashtags(prev => prev.filter((_, i) => i !== idx));
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    const tags = newGroupTags.split(/[\s,]+/).filter(t => t.length > 0);
    try {
      await axios.post(`${API}/hashtag-groups`, { name: newGroupName.trim(), hashtags: tags });
      setNewGroupName('');
      setNewGroupTags('');
      loadHashtagGroups();
      toast({ title: 'Hashtag group created' });
    } catch {
      toast({ title: 'Failed to create group', variant: 'destructive' });
    }
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      await axios.delete(`${API}/hashtag-groups/${groupId}`);
      loadHashtagGroups();
      toast({ title: 'Group deleted' });
    } catch {
      toast({ title: 'Failed to delete group', variant: 'destructive' });
    }
  };

  const handleUpdateGroup = async (groupId) => {
    if (!editingGroup) return;
    const tags = editingGroup.tagsText.split(/[\s,]+/).filter(t => t.length > 0);
    try {
      await axios.put(`${API}/hashtag-groups/${groupId}`, { name: editingGroup.name, hashtags: tags });
      setEditingGroup(null);
      loadHashtagGroups();
      toast({ title: 'Group updated' });
    } catch {
      toast({ title: 'Failed to update group', variant: 'destructive' });
    }
  };

  const handleSocialChange = (platform, value) => {
    setSocialHandles(prev => ({ ...prev, [platform]: value }));
  };

  const handleSave = async () => {
    if (!activeClient) return;
    setSaving(true);
    try {
      await axios.put(`${API}/clients/${activeClient.id}`, {
        name,
        logo_url: logoUrl || null,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        accent_color: accentColor,
        hashtags,
        social_handles: socialHandles,
        ayrshare_profile_key: ayrshareProfileKey || null,
        brand_voice: brandVoice || null,
      });
      await refreshClients();
      toast({ title: 'Settings saved', description: `${name} branding updated` });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to save', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (!activeClient) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  const SOCIAL_PLATFORMS = [
    { key: 'facebook', label: 'Facebook', placeholder: 'Page name or URL' },
    { key: 'instagram', label: 'Instagram', placeholder: '@handle' },
    { key: 'twitter', label: 'X / Twitter', placeholder: '@handle' },
    { key: 'linkedin', label: 'LinkedIn', placeholder: 'Company page URL' },
  ];

  return (
    <div className="min-h-screen page-background" data-testid="client-settings-page">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: primaryColor }}>
              Client Settings
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Branding, colors, and social accounts for <span className="font-semibold">{activeClient.name}</span>
            </p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="text-white"
            style={{ backgroundColor: primaryColor }}
            data-testid="save-settings-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        <div className="space-y-6">
          {/* Name & Logo */}
          <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <Image className="w-4 h-4 text-gray-400" /> Identity
            </h2>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Client Name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="text-base font-semibold"
                data-testid="client-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Logo</label>
              <div className="flex items-center gap-4">
                {logoPreview && (
                  <div className="w-16 h-16 rounded-xl border border-gray-200 flex items-center justify-center overflow-hidden bg-gray-50">
                    <img src={logoPreview} alt="Logo" className="w-14 h-14 object-contain" />
                  </div>
                )}
                <div className="flex-1">
                  <FileUpload onUploadComplete={handleLogoUpload} currentImage={logoPreview} />
                </div>
              </div>
            </div>
          </section>

          {/* Colors */}
          <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <Palette className="w-4 h-4 text-gray-400" /> Brand Colors
            </h2>
            {/* Preview bar */}
            <div className="flex rounded-lg overflow-hidden h-8">
              <div className="flex-1" style={{ backgroundColor: primaryColor }} />
              <div className="flex-1" style={{ backgroundColor: secondaryColor }} />
              <div className="flex-1" style={{ backgroundColor: accentColor }} />
            </div>
            <div className="grid gap-4">
              <ColorPicker label="Primary" value={primaryColor} onChange={setPrimaryColor} testId="color-primary" />
              <ColorPicker label="Secondary" value={secondaryColor} onChange={setSecondaryColor} testId="color-secondary" />
              <ColorPicker label="Accent" value={accentColor} onChange={setAccentColor} testId="color-accent" />
            </div>
          </section>

          {/* Brand Voice */}
          <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-gray-400" /> Brand Voice
            </h2>
            <p className="text-xs text-gray-400">
              Describe how this client should sound on social media. The AI will use this when generating posts.
            </p>
            <textarea
              value={brandVoice}
              onChange={(e) => setBrandVoice(e.target.value)}
              placeholder={"Describe the brand's personality and tone...\n\nExample:\nFriendly but professional. Community-focused. Write like a real person — short paragraphs, ask questions, celebrate staff. Say \"we\" and \"our team.\" Don't use corporate jargon or sound robotic."}
              rows={5}
              className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:outline-none"
              style={{ '--tw-ring-color': 'var(--brand-accent)' }}
              data-testid="brand-voice-input"
            />
          </section>

          {/* Client Portal Link */}
          <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4" data-testid="portal-link-section">
            <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <Link2 className="w-4 h-4 text-gray-400" /> Client Portal Link
            </h2>
            <p className="text-xs text-gray-400">
              Generate a private shareable link so your client can review upcoming posts, approve content, and submit ideas, no login required.
            </p>
            {portalUrl ? (
              <div className="space-y-4">
                {/* Branded URL display */}
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <p className="text-[10px] font-bold uppercase text-gray-400 tracking-wider mb-1.5">
                    {activeClient?.name}'s Portal Link
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      value={portalUrl}
                      readOnly
                      className="flex-1 text-xs font-mono bg-white h-9"
                      data-testid="portal-link-input"
                      onClick={(e) => e.target.select()}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyPortalLink}
                      className="h-9 px-3 shrink-0"
                      data-testid="copy-portal-link-btn"
                    >
                      {portalCopied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex flex-wrap items-center gap-3">
                  <a
                    href={portalUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg text-white"
                    style={{ backgroundColor: primaryColor }}
                    data-testid="open-portal-link"
                  >
                    <ExternalLink className="w-3 h-3" /> Open in New Tab
                  </a>
                  <a
                    href={`mailto:?subject=${encodeURIComponent(`Your ${activeClient?.name || 'business'} content review portal`)}&body=${encodeURIComponent(`Hi!\n\nHere's your private link to review and approve upcoming social media posts:\n\n${portalUrl}\n\nYou can:\n• Preview each post before it goes live\n• Approve posts, suggest edits, or mark posts to skip\n• Submit content ideas anytime\n\nNo login needed, just click the link!\n`)}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                    data-testid="email-portal-link"
                  >
                    <Mail className="w-3 h-3" /> Email to Client
                  </a>
                  <button
                    onClick={handleGeneratePortalToken}
                    disabled={portalLoading}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-gray-600 ml-auto"
                    data-testid="regenerate-portal-link-btn"
                  >
                    <RefreshCw className={`w-3 h-3 ${portalLoading ? 'animate-spin' : ''}`} /> Regenerate
                  </button>
                </div>

                <p className="text-[10px] text-amber-600 bg-amber-50 px-2.5 py-1.5 rounded-lg">
                  Regenerating will invalidate the previous link. Only share with trusted clients.
                </p>
              </div>
            ) : (
              <Button
                onClick={handleGeneratePortalToken}
                disabled={portalLoading}
                className="text-white"
                style={{ backgroundColor: primaryColor }}
                data-testid="generate-portal-link-btn"
              >
                {portalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                Generate Portal Link
              </Button>
            )}
          </section>

          {/* Portal Welcome Message */}
          <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4" data-testid="portal-welcome-section">
            <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-gray-400" /> Portal Welcome Message
            </h2>
            <p className="text-xs text-gray-400">
              A personal greeting shown at the top of your client's portal. Leave blank for no message.
            </p>
            <textarea
              value={portalWelcome}
              onChange={(e) => setPortalWelcome(e.target.value)}
              placeholder={`e.g. Hi ${activeClient?.name || 'team'}, here's your upcoming content for review!`}
              rows={2}
              className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-blue-200 focus:outline-none"
              data-testid="portal-welcome-input"
            />
            {portalWelcome !== (activeClient?.portal_welcome || '') && (
              <Button
                size="sm"
                onClick={async () => {
                  try {
                    await axios.put(`${API}/clients/${activeClient.id}`, { portal_welcome: portalWelcome }, { withCredentials: true });
                    toast({ title: 'Welcome message saved!' });
                  } catch {
                    toast({ title: 'Failed to save', variant: 'destructive' });
                  }
                }}
                className="text-white"
                style={{ backgroundColor: primaryColor }}
                data-testid="save-welcome-btn"
              >
                <Save className="w-3.5 h-3.5 mr-1.5" /> Save Message
              </Button>
            )}
          </section>

          {/* Portal Activity Log */}
          <PortalActivityLog clientId={activeClient?.id} primaryColor={primaryColor} />

          {/* Hashtags */}
          <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <Hash className="w-4 h-4 text-gray-400" /> Default Hashtags
            </h2>
            <p className="text-xs text-gray-400">These are appended to posts when publishing via Ayrshare.</p>
            <div className="flex flex-wrap gap-2">
              {hashtags.map((tag, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold text-white"
                  style={{ backgroundColor: primaryColor }}
                  data-testid={`hashtag-${idx}`}
                >
                  {tag}
                  <button onClick={() => handleRemoveHashtag(idx)} className="hover:opacity-70">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Input
                value={newHashtag}
                onChange={(e) => setNewHashtag(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddHashtag()}
                placeholder="Add hashtag..."
                className="flex-1 h-8 text-sm"
                data-testid="new-hashtag-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddHashtag}
                disabled={!newHashtag.trim()}
                data-testid="add-hashtag-btn"
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          </section>

          {/* Hashtag Groups */}
          <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <Hash className="w-4 h-4 text-gray-400" /> Hashtag Groups
            </h2>
            <p className="text-xs text-gray-400">Create groups of hashtags you can quick-insert when writing posts (e.g., "Travel", "Engagement").</p>

            {/* Existing groups */}
            <div className="space-y-3">
              {hashtagGroups.map(g => (
                <div key={g.id} className="border border-gray-100 rounded-lg p-3">
                  {editingGroup?.id === g.id ? (
                    <div className="space-y-2">
                      <Input
                        value={editingGroup.name}
                        onChange={e => setEditingGroup(prev => ({ ...prev, name: e.target.value }))}
                        className="h-8 text-sm font-semibold"
                        data-testid={`edit-group-name-${g.id}`}
                      />
                      <Input
                        value={editingGroup.tagsText}
                        onChange={e => setEditingGroup(prev => ({ ...prev, tagsText: e.target.value }))}
                        placeholder="#tag1 #tag2 #tag3"
                        className="h-8 text-xs font-mono"
                        data-testid={`edit-group-tags-${g.id}`}
                      />
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditingGroup(null)} className="text-xs h-7">Cancel</Button>
                        <Button size="sm" onClick={() => handleUpdateGroup(g.id)} className="text-xs h-7 text-white" style={{ backgroundColor: 'var(--brand-primary)' }}>Save</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-2">
                      <div
                        className="flex-1 cursor-pointer"
                        onClick={() => setEditingGroup({ id: g.id, name: g.name, tagsText: g.hashtags.join(' ') })}
                      >
                        <span className="text-sm font-semibold text-gray-800">{g.name}</span>
                        <p className="text-xs text-gray-400 mt-0.5">{g.hashtags.join(' ')}</p>
                      </div>
                      <button
                        onClick={() => handleDeleteGroup(g.id)}
                        className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                        data-testid={`delete-group-${g.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Create new group */}
            <div className="border-t border-gray-100 pt-3 space-y-2">
              <Input
                value={newGroupName}
                onChange={e => setNewGroupName(e.target.value)}
                placeholder="Group name (e.g., Travel, Engagement)"
                className="h-8 text-sm"
                data-testid="new-group-name-input"
              />
              <Input
                value={newGroupTags}
                onChange={e => setNewGroupTags(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreateGroup()}
                placeholder="#hashtag1 #hashtag2 #hashtag3"
                className="h-8 text-xs font-mono"
                data-testid="new-group-tags-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleCreateGroup}
                disabled={!newGroupName.trim()}
                data-testid="create-group-btn"
              >
                <Plus className="w-3 h-3 mr-1" /> Create Group
              </Button>
            </div>
          </section>

          {/* Social Handles */}
          <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <AtSign className="w-4 h-4 text-gray-400" /> Social Handles
            </h2>
            <p className="text-xs text-gray-400">Used in post previews and publishing.</p>
            <div className="grid gap-3">
              {SOCIAL_PLATFORMS.map(p => (
                <div key={p.key} className="flex items-center gap-3">
                  <label className="text-xs font-semibold text-gray-500 w-20">{p.label}</label>
                  <Input
                    value={socialHandles[p.key] || ''}
                    onChange={(e) => handleSocialChange(p.key, e.target.value)}
                    placeholder={p.placeholder}
                    className="flex-1 h-8 text-sm"
                    data-testid={`social-${p.key}`}
                  />
                </div>
              ))}
            </div>
          </section>

          {/* Social Publishing */}
          <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <ExternalLink className="w-4 h-4 text-gray-400" /> Social Publishing (Ayrshare)
            </h2>
            <p className="text-xs text-gray-400">
              Connect this client to Ayrshare for direct Facebook/Instagram publishing. Each client needs its own Profile Key from your Ayrshare dashboard.
            </p>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">
                Ayrshare Profile Key
              </label>
              <Input
                value={ayrshareProfileKey}
                onChange={(e) => setAyrshareProfileKey(e.target.value)}
                placeholder="Paste your Profile Key here (e.g. D9AB94DE-A56F43E4-...)"
                className="font-mono text-sm"
                data-testid="ayrshare-profile-key-input"
              />
              <p className="text-[10px] text-gray-400 mt-1.5">
                Find this in <a href="https://app.ayrshare.com/api" target="_blank" rel="noreferrer" className="underline">Ayrshare → Profile Dashboard → Profile Key</a>
              </p>
            </div>
            <ConnectedAccounts clientKey={`${activeClient.id}-${activeClient.ayrshare_profile_key || ''}`} />
            <div className="flex items-center gap-2 pt-1">
              <a
                href="https://app.ayrshare.com/social-accounts"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                data-testid="ayrshare-settings-link"
              >
                Connect Social Accounts <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </section>

          {/* Danger Zone */}
          <section className="bg-white rounded-xl border-2 border-red-200 p-5 space-y-4" data-testid="danger-zone-section">
            <h2 className="text-sm font-bold text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Danger Zone
            </h2>
            <p className="text-xs text-gray-500">
              Permanently delete <strong>{activeClient?.name}</strong> and all of its data including posts, images, ideas, and scrape history. This action cannot be undone.
            </p>
            {!deleteConfirmOpen ? (
              <Button
                variant="outline"
                onClick={() => setDeleteConfirmOpen(true)}
                className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                data-testid="delete-client-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete This Client
              </Button>
            ) : (
              <div className="space-y-3 p-4 bg-red-50 rounded-lg border border-red-200">
                <p className="text-xs font-medium text-red-800">
                  Type <strong>{activeClient?.name}</strong> to confirm:
                </p>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={activeClient?.name}
                  className="border-red-300 focus:ring-red-400 text-sm"
                  data-testid="delete-confirm-input"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={handleDeleteClient}
                    disabled={deleteConfirmText !== activeClient?.name || deleteLoading}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="confirm-delete-client-btn"
                  >
                    {deleteLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    Permanently Delete
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => { setDeleteConfirmOpen(false); setDeleteConfirmText(''); }}
                    className="text-gray-500"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
